import { Badge, EmptyState, Skeleton } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import { outputFileName, outputKindLabel, outputSizeLabel, outputTotalSizeLabel } from "../logic/outputs.ts";

export interface ConversationOutputEntry {
  readonly path: string;
  readonly snapshotFileId: string;
  readonly mimeType: string;
  readonly size: number;
  readonly version: number;
  readonly hash: string;
  readonly runId: string;
  readonly branchId: string;
}

export interface ConversationOutputsPanelProps {
  readonly outputs: readonly ConversationOutputEntry[];
  readonly branchLabel?: string | null;
  readonly truncated?: boolean;
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly downloadingFileId?: string | null;
  readonly downloadError?: string | null;
  readonly emptyHint?: string;
  readonly onDownload: (output: ConversationOutputEntry) => void | Promise<void>;
  readonly onRefresh?: () => void;
}

export function ConversationOutputsPanel({
  outputs,
  branchLabel = null,
  truncated = false,
  loading = false,
  error = null,
  downloadingFileId = null,
  downloadError = null,
  emptyHint,
  onDownload,
  onRefresh,
}: ConversationOutputsPanelProps) {
  return (
    <section aria-label="Conversation outputs" style={stackStyle("var(--dayvise-space-md)")}>
      <div style={rowStyle("var(--dayvise-space-sm)")}>
        <h2 style={{ margin: 0, fontFamily: "var(--dayvise-font-family-sans)", fontSize: "var(--dayvise-font-size-lg)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>Outputs</h2>
        {outputs.length > 0 ? <Badge>{`${outputs.length} file${outputs.length === 1 ? "" : "s"} · ${outputTotalSizeLabel(outputs)}`}</Badge> : null}
        {onRefresh ? (
          <Button size="sm" variant="ghost" disabled={loading} onClick={() => onRefresh()}>
            Refresh
          </Button>
        ) : null}
      </div>

      {branchLabel ? (
        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
          {`Published outputs on branch ${branchLabel}. Downloads return the exact published bytes of that revision.`}
        </span>
      ) : null}

      {error ? (
        <p role="alert" style={{ ...MUTED_TEXT, margin: 0, color: "var(--dayvise-color-danger)" }}>
          {`Outputs could not be loaded: ${error}`}
        </p>
      ) : null}

      {downloadError ? (
        <p role="alert" style={{ ...MUTED_TEXT, margin: 0, color: "var(--dayvise-color-danger)" }}>
          {downloadError}
        </p>
      ) : null}

      {loading && outputs.length === 0 ? (
        <div role="status" aria-label="Loading outputs" style={stackStyle("var(--dayvise-space-sm)")}>
          <Skeleton height="48px" />
          <Skeleton height="48px" />
        </div>
      ) : null}

      {!loading && !error && outputs.length === 0 ? (
        <EmptyState
          title="No published outputs yet"
          description={emptyHint ?? "When a run finishes with project files captured, its outputs appear here for download."}
        />
      ) : null}

      {outputs.length > 0 ? (
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          {outputs.map((output) => (
            <OutputCard
              key={`${output.branchId}:${output.snapshotFileId}`}
              output={output}
              downloading={downloadingFileId === output.snapshotFileId}
              busy={downloadingFileId !== null}
              onDownload={onDownload}
            />
          ))}
        </div>
      ) : null}

      {truncated ? (
        <p style={{ ...MUTED_TEXT, margin: 0 }}>
          Only the first entries are shown. Download individual files, or refine the request to a smaller project.
        </p>
      ) : null}
    </section>
  );
}

interface OutputCardProps {
  readonly output: ConversationOutputEntry;
  readonly downloading: boolean;
  readonly busy: boolean;
  readonly onDownload: (output: ConversationOutputEntry) => void | Promise<void>;
}

function OutputCard({ output, downloading, busy, onDownload }: OutputCardProps) {
  const kind = outputKindLabel(output.mimeType);
  return (
    <Card
      title={output.path}
      subtitle={`${kind} · ${outputSizeLabel(output.size)} · revision ${output.version}`}
      footer={
        <div style={rowStyle("var(--dayvise-space-xs)")}>
          <Button size="sm" loading={downloading} disabled={busy && !downloading} onClick={() => void onDownload(output)}>
            {`Download ${outputFileName(output.path)}`}
          </Button>
          {output.runId ? (
            <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{`from run ${output.runId}`}</span>
          ) : null}
        </div>
      }
    >
      <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
        <Badge tone="neutral">{output.mimeType}</Badge>
        {output.size === 0 ? <Badge tone="neutral">Empty file</Badge> : null}
        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{output.hash.slice(0, 12)}</span>
      </div>
    </Card>
  );
}
